import React, { useState, useEffect } from "react";
import { db } from "../../../firebase";
import { useAuth } from "../../../context/AuthProvider";
import {
    collection,
    addDoc,
    serverTimestamp,
    getDocs,
    query,
    orderBy,
} from "firebase/firestore";
import { calculatePaces } from "../lib/paceCalculator";

const SixMinTestForm = () => {
    const { user } = useAuth();
    const [minutes, setMinutes] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [unit, setUnit] = useState("min/km");
    const [error, setError] = useState("");
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchTests = async () => {
            if (!user) return;

            try {
                const testsRef = collection(db, "users", user.uid, "sixMinTests");
                const q = query(testsRef, orderBy("createdAt", "desc"));
                const querySnapshot = await getDocs(q);

                const testsData = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                setTests(testsData);
            } catch (err) {
                console.error("Error fetching test history:", err?.message || err);
                setError("Failed to load test history. Please refresh the page.");
            }
        };

        fetchTests();
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!user) {
            setError("You must be logged in to submit a test.");
            return;
        }

        if (minutes < 0 || seconds < 0 || seconds >= 60) {
            setError("Please enter valid minutes and seconds (0-59).");
            return;
        }

        setLoading(true);

        try {
            const testsRef = collection(db, "users", user.uid, "sixMinTests");

            await addDoc(testsRef, {
                minutes,
                seconds,
                unit,
                createdAt: serverTimestamp(),
            });

            setMinutes(0);
            setSeconds(0);

            // Refresh tests
            const q = query(testsRef, orderBy("createdAt", "desc"));
            const querySnapshot = await getDocs(q);

            const testsData = querySnapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            setTests(testsData);
        } catch (err) {
            console.error("Error submitting test:", err?.message || err);

            // Provide more specific error messages based on error type
            if (err?.code === "permission-denied") {
                setError("You don't have permission to submit tests. Please sign in again.");
            } else if (err?.code === "unavailable") {
                setError("Unable to connect to the server. Please check your internet connection.");
            } else {
                setError("Failed to submit test. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-2 sm:p-2 max-w-xl mx-auto">
            <h2 className="text-2xl font-bold mb-4 text-center">6-Minute Test</h2>

            <form onSubmit={handleSubmit} className="mb-4 space-y-3">
                {error && <p className="text-red-500 text-center">{error}</p>}

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-sm font-medium mb-1">Minutes:</label>
                        <input
                            type="number"
                            value={minutes}
                            onChange={(e) => setMinutes(Number(e.target.value))}
                            className="border p-2 rounded w-full"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Seconds:</label>
                        <input
                            type="number"
                            value={seconds}
                            onChange={(e) => setSeconds(Number(e.target.value))}
                            className="border p-2 rounded w-full"
                            min="0"
                            max="59"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Unit:</label>
                        <select
                            value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                            className="border p-2 rounded w-full"
                        >
                            <option value="min/km">min/km</option>
                            <option value="min/mile">min/mile</option>
                        </select>
                    </div>
                </div>

                <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 transition-colors text-white px-4 py-2 rounded w-full"
                    disabled={loading}
                >
                    {loading ? "Submitting..." : "Submit Test"}
                </button>
            </form>

            <div className="text-sm space-y-2 py-2">
                <p>
                    The first step to be able to create a personalized training plan is to
                    know your current running fitness level. There is no better test in
                    the world than the 6-minute test.
                </p>

                <p>
                    <a
                        href="https://www.youtube.com/watch?v=MVOh2aFhiaA&t=1s"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 underline"
                    >
                        Watch this video
                    </a>{" "}
                    to learn how to perform it. Then come back and use the form below to
                    input your average pace for the test.
                </p>
            </div>

            <h3 className="text-xl font-semibold mb-3">Test History</h3>

            {tests.length === 0 ? (
                <p className="text-center">No tests submitted yet.</p>
            ) : (
                <ul className="space-y-3">
                    {tests.map((test) => {
                        const paces = calculatePaces(
                            test.minutes,
                            test.seconds,
                            test.unit
                        );

                        return (
                            <li key={test.id} className="p-3 border rounded shadow-sm">
                                <p className="text-sm">
                                    <strong>Date:</strong>{" "}
                                    {test.createdAt?.toDate()?.toLocaleString() || "Pending..."}
                                </p>

                                <p className="text-green-500 text-sm mt-1">
                                    <strong>Test Pace (vVO2max):</strong> {paces.vVO2max}{" "}
                                    {test.unit}
                                </p>

                                <p className="text-sm mt-1">
                                    <strong>Easy Run Pace:</strong> {paces.easyRun.low} -{" "}
                                    {paces.easyRun.high} {test.unit}
                                </p>

                                <p className="text-sm mt-1">
                                    <strong>Threshold Pace:</strong> {paces.threshold} {test.unit}
                                </p>

                                <p className="text-sm mt-1">
                                    <strong>Long Run Pace:</strong> {paces.longRun} {test.unit}
                                </p>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default SixMinTestForm;
